import { ArrowRight } from "@phosphor-icons/react"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import type { GetStaticProps } from "next"
import Link from "next/link"
import Container from "@/components/common/Container"
import Layout from "@/components/common/Layout"
import MarketingHeading from "@/components/common/MarketingHeading"
import { PageMeta } from "@/components/common/Seo/PageMeta"
import { getJobPostingsByTeam, type JobPostingMeta } from "@/utils/careers-mdx"

interface CareersPageProps {
  jobPostingsByTeam: Record<string, JobPostingMeta[]>
}

export const getStaticProps: GetStaticProps<CareersPageProps> = async () => {
  const jobPostingsByTeam = getJobPostingsByTeam()

  return {
    props: {
      jobPostingsByTeam,
    },
  }
}

export default function CareersPage({ jobPostingsByTeam }: CareersPageProps) {
  return (
    <Layout>
      <PageMeta
        meta={{
          title: "Careers",
          description:
            "Join Token Terminal and build the future of data-driven, onchain decision making. We hire remote builders in data, engineering and GTM. See our open positions.",
        }}
      />
      <div className="py-12 max-w-[860px] mx-auto">
        <Container>
          <div className="flex flex-col items-start gap-0 mb-12">
            <MarketingHeading
              level={1}
              className="text-3xl md:text-5xl md:leading-none leading-tight mb-5"
            >
              Open positions
            </MarketingHeading>
            <p className="text-base text-fg-secondary text-balance">
              We are a team of builders, thinkers, and doers. We are looking for people who want to
              build the future of data-driven, onchain decision making.
            </p>
          </div>

          <div className="flex flex-col gap-6">
            {Object.entries(jobPostingsByTeam).map(([team, roles]) => (
              <div key={team} className="flex flex-col gap-2">
                <h3 className="font-medium text-fg-secondary">{team}</h3>
                {roles.map((role) => (
                  <Link
                    key={role.slug}
                    href={`/careers/${role.slug}`}
                    className="block no-underline"
                  >
                    <Card className="p-6 rounded-2xl hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                      <div className="flex justify-between items-center w-full">
                        <div className="flex flex-col">
                          <p className="font-medium text-fg-default">{role.title}</p>
                          <p className="text-sm text-fg-secondary">{role.location}</p>
                        </div>
                        <ArrowRight size={20} className="text-fg-secondary" />
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ))}

            {Object.keys(jobPostingsByTeam).length === 0 && (
              <div className="p-6 text-center">
                <p className="text-gray-400">
                  We don&apos;t have any open positions at the moment. Please check back later!
                </p>
              </div>
            )}
          </div>
        </Container>
      </div>
    </Layout>
  )
}
